import { Box, Grid, Paper, Typography } from '@mui/material';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { Table, TableBody, TableHead, TableRow } from '@mui/material';
import { HeadTableCell, RowTableCell, GeneralTableBox } from '../../../common/components';
import { formatCurrency } from '../../../utils/formatUtils';

/**
 * Seccion reusable con tabla y grafico de barras.
 * @param {Object} props - Props del componente.
 * @param {string} props.title - Titulo principal.
 * @param {Array<Object>} props.columns - Columnas para la tabla.
 * @param {Array<Object>} props.rows - Filas de la tabla.
 * @param {Array<Object>} props.chartData - Data para el grafico.
 * @param {string} props.chartId - Identificador del grafico.
 * @param {string} [props.xAxisLabel] - Etiqueta para el eje X.
 * @param {Function} [props.valueFormatter] - Formateador de valores.
 * @returns {JSX.Element} Seccion con tabla y grafico.
 */
const GerencialesBarTableSection = ({
  title,
  columns,
  rows,
  chartData,
  chartId,
  xAxisLabel = 'Mora promedio $',
  valueFormatter = formatCurrency,
}) => {
  const hasData = rows.length > 0;

  return (
    <Paper elevation={1} sx={{ mb: 1, mt: 1 }}>
      <Box
        sx={{
          backgroundColor: '#3b3f46',
          color: 'white',
          px: 2.5,
          py: 1.5,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
          fontSize: 30,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <GeneralTableBox>
              <Table size="large">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <HeadTableCell
                        key={column.key}
                        sx={{ textAlign: column.align || 'left', color: 'white' }}
                      >
                        {column.label}
                      </HeadTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hasData ? (
                    rows.map((row, rowIndex) => (
                      <TableRow key={`${rowIndex}-${row?.label || rowIndex}`}>
                        {columns.map((column) => (
                          <RowTableCell
                            key={`${rowIndex}-${column.key}`}
                            sx={{
                              textAlign: column.align || 'left',
                              fontWeight: row?.is_total ? 'bold' : 'normal',
                            }}
                          >
                            {row?.[column.key] ?? '-'}
                          </RowTableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <RowTableCell colSpan={columns.length} sx={{ textAlign: 'center' }}>
                        Sin resultados
                      </RowTableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </GeneralTableBox>
          </Grid>

          <Grid item xs={12} md={7}>
            <Box id={chartId} sx={{ height: { xs: 280, md: 320 } }}>
              {hasData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    layout="vertical"
                    margin={{ top: 10, right: 20, left: 20, bottom: 30 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      tickFormatter={(value) => valueFormatter(value)}
                      label={{
                        value: xAxisLabel,
                        position: 'insideBottom',
                        offset: -5,
                      }}
                    />
                    <YAxis type="category" dataKey="name" width={120} />
                    <RechartsTooltip
                      formatter={(value) => valueFormatter(value)}
                      labelFormatter={() => ''}
                    />
                    <Bar dataKey="value" radius={[6, 6, 6, 6]}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Sin datos para graficar
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default GerencialesBarTableSection;
